import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import {
  BookkeeperCompanyIncidenceOptionFragment,
  BookkeeperUpdateIncidenceOptionSettingsDocument,
  BookkeepersCompanyIncidenceOptionsDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { FACTORIAL_APP } from 'modules/bookkeepers/lib/amplitudeData'
import { createIncidenceOptionSettings } from 'modules/bookkeepers/lib/incidenceOptions'
import { trackIncidenceOptionSettings } from 'modules/bookkeepers/lib/incidenceOptions'
import { IncidenceOptionsType } from 'modules/bookkeepers/lib/incidenceOptions'
import {
  IncidenceOptionSection,
  SECTION_NAMES,
} from 'modules/payroll_bundle/hooks/useIncidenceOptionSections'
import { INCIDENCE_TYPES } from 'modules/payroll_bundle/lib/Incidences/Types'

import { IncidenceOptionsSection } from './_IncidenceOptionsSection'

interface Props {
  companyId: string
  companyName: string
  incidenceOptionSections: IncidenceOptionSection[]
  companyIncidenceSettings: BookkeeperCompanyIncidenceOptionFragment[]
}

export const Settings = observer(
  ({ companyId, companyName, incidenceOptionSections, companyIncidenceSettings }: Props) => {
    const leaveIncidenceSection = incidenceOptionSections.find(
      (section) => section.id === SECTION_NAMES.absence_updates
    )
    const leaveIncidenceOptions = leaveIncidenceSection?.incidenceOptions || []

    const leaveIncidenceName = (leaveIdentifier: string) => {
      const nonCustomLeave = [INCIDENCE_TYPES.sick, INCIDENCE_TYPES.parental]

      if (nonCustomLeave.includes(leaveIdentifier)) return leaveIdentifier

      return INCIDENCE_TYPES.custom_leave
    }
    const [loadingIncidenceOption, setLoadingIncidenceOption] = React.useState<boolean>(false)

    const [updateIncidenceOptionSettings] = useMutation(
      BookkeeperUpdateIncidenceOptionSettingsDocument,
      {
        extractorFn: (data) =>
          data.bookkeepersManagementMutations.patchBookkeepersmanagementOptionIncidenceOption,
      }
    )

    const toggleIncidenceOption = async (
      id: string,
      trackIncidence: boolean,
      needsValidation: boolean
    ) => {
      const result = await updateIncidenceOptionSettings({
        variables: {
          id: id,
          trackIncidence: trackIncidence,
          needsValidation: needsValidation,
        },
        refetchQueries: [BookkeepersCompanyIncidenceOptionsDocument],
      })

      const handled = handleResult(result, (_errors) => {})

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('app_store_v2.apps.payroll_bundle.settings.incidences.options.success'),
          time: 2000,
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('app_store_v2.apps.payroll_bundle.settings.incidences.options.error'),
          time: 2000,
        })
      }

      return handled
    }

    const incidenceOptionChange = async (type: IncidenceOptionsType, isValidation = false) => {
      const companyIncidenceOptionSettings = companyIncidenceSettings.find(
        (model) => model.incidenceName === type.id || model.leaveTypeId === String(type.id)
      )
      const leave = leaveIncidenceOptions.find(({ id }) => id === type.id)
      const isLeaveIncidence = leave !== undefined
      const incidenceName = isLeaveIncidence ? leaveIncidenceName(leave.identifier) : type.id
      const isBookkeeperView = false
      setLoadingIncidenceOption(true)
      if (companyIncidenceOptionSettings && companyIncidenceOptionSettings.id) {
        const tracking = isValidation
          ? companyIncidenceOptionSettings.trackIncidence
          : !companyIncidenceOptionSettings.trackIncidence
        const validating = isValidation
          ? !companyIncidenceOptionSettings.needsValidation
          : companyIncidenceOptionSettings.needsValidation
        toggleIncidenceOption(companyIncidenceOptionSettings.id, tracking, validating)
      } else {
        await createIncidenceOptionSettings(
          incidenceName,
          companyId,
          isLeaveIncidence ? type.id : null,
          isBookkeeperView,
          isValidation
        )
      }
      setLoadingIncidenceOption(false)
      trackIncidenceOptionSettings(
        companyName,
        incidenceName,
        isValidation,
        FACTORIAL_APP,
        companyIncidenceOptionSettings
      )
    }

    return (
      <Box width="full" gap="s12">
        {incidenceOptionSections.map((incidenceOptionSection, key) =>
          incidenceOptionSection.incidenceOptions.length === 0 ? null : (
            <IncidenceOptionsSection
              key={key}
              incidenceOptions={incidenceOptionSection.incidenceOptions}
              title={incidenceOptionSection.title}
              icon={incidenceOptionSection.icon}
              sectionReference={incidenceOptionSection.sectionReference}
              onTogglerChange={incidenceOptionChange}
              companyIncidenceSettings={companyIncidenceSettings}
              onLoadingIncidenceOption={loadingIncidenceOption}
            />
          )
        )}
      </Box>
    )
  }
)
